import React from "react";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

/**
 * WCAG APG roving-tabindex keyboard model for a `[role="tablist"]`, shared by
 * the tempurpedic tab blocks. The hook owns the active-tab index and hands back
 * per-tab props (roving `tabIndex`, `aria-selected`, and click / keydown
 * handlers) plus an element ref; each consumer maps `activeIndex` to its own
 * panel-visibility markup (a `hidden` attribute or an `--is-active` class).
 *
 * Keyboard: ArrowLeft / ArrowRight move-and-activate with wraparound, Home /
 * End jump to the first / last tab, and Space activates the focused tab (Enter
 * activates natively through the click handler). Arrow / Home / End also move
 * DOM focus to the newly-active tab, which is the only tab in the page tab
 * order (roving tabindex).
 */

// Space keys (WebKit historically reported "Spacebar"). Enter is intentionally
// omitted: both <button> and <a> activate on Enter via a native click, which
// the click handler already services.
const ACTIVATION_KEYS = new Set([" ", "Spacebar"]);

interface UseRovingTabListOptions {
    /** Number of tabs in the list. */
    readonly count: number;
    /** Index of the initially-active tab (default 0). */
    readonly initialActiveIndex?: number;
    /**
     * Keep the active tab scrolled into view inside a horizontally-scrolling
     * tablist. Attach the returned `tabListRef` to the `[role="tablist"]`
     * container to supply the scroll context.
     */
    readonly scrollActiveIntoView?: boolean;
}

interface RovingTabProps {
    "ref": (el: HTMLElement | null) => void;
    "tabIndex": 0 | -1;
    "aria-selected": "true" | "false";
    "onClick": (event: React.MouseEvent) => void;
    "onKeyDown": (event: React.KeyboardEvent) => void;
}

interface UseRovingTabListResult {
    activeIndex: number;
    setActiveIndex: (index: number) => void;
    getTabProps: (index: number) => RovingTabProps;
    /**
     * Ref callback for the `[role="tablist"]` container, used to keep the
     * active tab scrolled into view. A callback (not a ref object) so it
     * attaches to any host element type without a per-caller annotation.
     */
    tabListRef: (el: HTMLElement | null) => void;
}

// Nudge the tablist so a tab clipped by either viewport edge becomes fully
// visible (24px of breathing room), reproducing the legacy ui/tabs.ts scroll.
const scrollTabIntoView = (
    tab: HTMLElement,
    tabList: HTMLElement | null,
): void => {
    const window = getWindow();
    if (!window || !tabList) {
        return;
    }
    const rect = tab.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
        tabList.scrollLeft += rect.right - window.innerWidth + 24;
    }
    if (rect.left < 0) {
        tabList.scrollLeft += rect.left - 24;
    }
};

export const useRovingTabList = (
    options: UseRovingTabListOptions,
): UseRovingTabListResult => {
    const {
        count,
        initialActiveIndex = 0,
        scrollActiveIntoView = false,
    } = options;
    const [activeIndex, setActiveIndex] = React.useState(initialActiveIndex);
    const tabRefs = React.useRef<(HTMLElement | null)[]>([]);
    const tabListElemRef = React.useRef<HTMLElement | null>(null);
    const tabListRef = React.useCallback((el: HTMLElement | null) => {
        tabListElemRef.current = el;
    }, []);

    // Keep the active tab visible on every activation path (click, keyboard,
    // or a consumer's on-mount hash sync). Post-hydration only, so the ref is
    // populated and there is no SSR layout thrash.
    React.useEffect(() => {
        if (!scrollActiveIntoView) {
            return;
        }
        const tab = tabRefs.current[activeIndex];
        if (tab) {
            scrollTabIntoView(tab, tabListElemRef.current);
        }
    }, [activeIndex, scrollActiveIntoView]);

    const activate = (index: number, focus: boolean): void => {
        setActiveIndex(index);
        if (focus) {
            tabRefs.current[index]?.focus();
        }
    };

    const getTabProps = (index: number): RovingTabProps => ({
        "ref": (el) => {
            tabRefs.current[index] = el;
        },
        "tabIndex": index === activeIndex ? 0 : -1,
        "aria-selected": index === activeIndex ? "true" : "false",
        "onClick": (event) => {
            event.preventDefault();
            activate(index, false);
        },
        "onKeyDown": (event) => {
            switch (event.key) {
                case "ArrowLeft":
                    event.preventDefault();
                    activate(index === 0 ? count - 1 : index - 1, true);
                    break;
                case "ArrowRight":
                    event.preventDefault();
                    activate(index === count - 1 ? 0 : index + 1, true);
                    break;
                case "Home":
                    event.preventDefault();
                    activate(0, true);
                    break;
                case "End":
                    event.preventDefault();
                    activate(count - 1, true);
                    break;
                default:
                    if (ACTIVATION_KEYS.has(event.key)) {
                        event.preventDefault();
                        activate(index, false);
                    }
            }
        },
    });

    return { activeIndex, setActiveIndex, getTabProps, tabListRef };
};
